import { TeamGroupTeamDto, TeamGroupTeamInput } from 'src/api/teamGroups';
import { ContactListMember, InformationContentType, Team } from '../types';
import { hasDeclinedContactShare } from './contactListMembers';

export type EditableTeam = {
  key: string;
  _id?: string;
  name: string;
  memberIds: string[];
};

export const createTeamKey = () =>
  `team-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const normalizeInformationContentTypes = (
  contentTypes?: string[]
): InformationContentType[] =>
  (contentTypes ?? ['text', 'files']).map((type) =>
    type === 'teamList' ? 'teams' : type
  ) as InformationContentType[];

export const createEditableTeam = (
  partial?: Partial<EditableTeam> & { index?: number }
): EditableTeam => ({
  key: partial?.key ?? createTeamKey(),
  _id: partial?._id,
  name: partial?.name ?? `Lag ${(partial?.index ?? 0) + 1}`,
  memberIds: [...(partial?.memberIds ?? [])],
});

export const toEditableTeams = (teams: TeamGroupTeamDto[]): EditableTeam[] =>
  teams.map((team, index) =>
    createEditableTeam({
      _id: team._id,
      name: team.name,
      memberIds: team.memberIds,
      index,
    })
  );

export const toPutTeams = (teams: EditableTeam[]): TeamGroupTeamInput[] =>
  teams.map((team) => ({
    ...(team._id ? { _id: team._id } : {}),
    name: team.name.trim() || 'Lag',
    memberIds: [...team.memberIds],
  }));

export const getContactListExclusions = (
  sections: { contentTypes: string[]; excludedContactIds: string[] }[]
): string[] => {
  const ids = new Set<string>();
  sections.forEach((section) => {
    if (!section.contentTypes.includes('contactList')) {
      return;
    }
    section.excludedContactIds.forEach((id) => ids.add(id));
  });
  return Array.from(ids);
};

export const getAssignableContacts = (
  contacts: ContactListMember[],
  excludedIds: string[] = []
): ContactListMember[] => {
  const excluded = new Set(excludedIds);
  return contacts.filter(
    (contact) => !excluded.has(contact.id) || hasDeclinedContactShare(contact)
  );
};

export const getTeamEditorContacts = getAssignableContacts;

export const getAssignedMemberIds = (teams: EditableTeam[]): Set<string> => {
  const ids = new Set<string>();
  teams.forEach((team) => {
    team.memberIds.forEach((id) => ids.add(id));
  });
  return ids;
};

export const getUnassignedMembers = (
  contacts: ContactListMember[],
  teams: EditableTeam[]
): ContactListMember[] => {
  const assigned = getAssignedMemberIds(teams);
  return contacts.filter((contact) => !assigned.has(contact.id));
};

export const getDuplicateMemberIds = (teams: EditableTeam[]): string[] => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  teams.forEach((team) => {
    team.memberIds.forEach((id) => {
      if (seen.has(id)) {
        duplicates.add(id);
      }
      seen.add(id);
    });
  });
  return Array.from(duplicates);
};

export const moveMemberToTeam = (
  teams: EditableTeam[],
  memberId: string,
  toTeamKey: string | 'unassigned',
  atIndex?: number
): EditableTeam[] =>
  teams.map((team) => {
    const withoutMember = team.memberIds.filter((id) => id !== memberId);
    if (toTeamKey !== 'unassigned' && team.key === toTeamKey) {
      const next = [...withoutMember];
      const index =
        atIndex == null
          ? next.length
          : Math.max(0, Math.min(atIndex, next.length));
      next.splice(index, 0, memberId);
      return {
        ...team,
        memberIds: next,
      };
    }
    return { ...team, memberIds: withoutMember };
  });

export const filterMemberIdsToPool = (
  memberIds: string[],
  poolIds: Set<string>
): string[] => memberIds.filter((id) => poolIds.has(id));

export const applyPreviousTeams = (
  previousTeams: TeamGroupTeamDto[],
  poolIds: Set<string>
): EditableTeam[] =>
  toEditableTeams(previousTeams).map((team) => ({
    ...team,
    memberIds: filterMemberIdsToPool(team.memberIds, poolIds),
  }));

export type AutoDistributeMode = 'teamCount' | 'membersPerTeam';

export type AutoDistributeStrategy =
  'even' | 'keepCompanyTogether' | 'balanceByCompany';

export type AutoDistributeOptions = {
  mode?: AutoDistributeMode;
  teamCount?: number;
  membersPerTeam?: number;
  strategy?: AutoDistributeStrategy;
  existingTeams?: EditableTeam[];
};

const companyGroupKey = (member: ContactListMember) => {
  const company = member.company?.trim().toLowerCase();
  return company ? `company:${company}` : `solo:${member.id}`;
};

const groupContactsByCompany = (pool: ContactListMember[]) => {
  const groups = new Map<string, ContactListMember[]>();
  pool.forEach((member) => {
    const key = companyGroupKey(member);
    const group = groups.get(key);
    if (group) {
      group.push(member);
    } else {
      groups.set(key, [member]);
    }
  });
  return Array.from(groups.values()).sort((a, b) => b.length - a.length);
};

const smallestTeamIndex = (teams: EditableTeam[]) =>
  teams.reduce((best, team, index) => {
    if (team.memberIds.length < teams[best].memberIds.length) {
      return index;
    }
    return best;
  }, 0);

export const resolveAutoTeamCount = (
  poolSize: number,
  mode: AutoDistributeMode,
  teamCount: number,
  membersPerTeam: number
): number => {
  if (mode === 'membersPerTeam') {
    const perTeam = Math.max(1, Math.floor(membersPerTeam));
    return Math.max(1, Math.ceil(Math.max(poolSize, 1) / perTeam));
  }

  const requested = Math.max(1, Math.floor(teamCount));
  if (poolSize <= 0) {
    return requested;
  }
  return Math.min(requested, poolSize);
};

export const perTeamRangeLabel = (counts: number[]) => {
  if (counts.length === 0) {
    return '0 deltakere per lag';
  }
  const min = Math.min(...counts);
  const max = Math.max(...counts);
  const unit = (count: number) =>
    count === 1 ? '1 deltaker' : `${count} deltakere`;
  if (min === max) {
    return `${unit(min)} per lag`;
  }
  return `${min}–${max} deltakere per lag`;
};

export const autoDistribute = (
  pool: ContactListMember[],
  teamCount: number,
  existingTeams: EditableTeam[] = [],
  strategy: AutoDistributeStrategy = 'even'
): EditableTeam[] =>
  autoDistributeWithOptions(pool, {
    mode: 'teamCount',
    teamCount,
    existingTeams,
    strategy,
  });

export const autoDistributeWithOptions = (
  pool: ContactListMember[],
  options: AutoDistributeOptions = {}
): EditableTeam[] => {
  const assignable = getAssignableContacts(pool);
  const count = resolveAutoTeamCount(
    assignable.length,
    options.mode ?? 'teamCount',
    options.teamCount ?? 2,
    options.membersPerTeam ?? 4
  );
  const existingTeams = options.existingTeams ?? [];
  const teams = Array.from({ length: count }, (_, index) => {
    const existing = existingTeams[index];
    return createEditableTeam({
      key: existing?.key,
      _id: existing?._id,
      name: existing?.name ?? `Lag ${index + 1}`,
      memberIds: [],
      index,
    });
  });

  const strategy = options.strategy ?? 'even';
  if (strategy === 'keepCompanyTogether') {
    groupContactsByCompany(assignable).forEach((group) => {
      const index = smallestTeamIndex(teams);
      group.forEach((member) => teams[index].memberIds.push(member.id));
    });
    return teams;
  }

  if (strategy === 'balanceByCompany') {
    groupContactsByCompany(assignable).forEach((group) => {
      group.forEach((member) => {
        const index = smallestTeamIndex(teams);
        teams[index].memberIds.push(member.id);
      });
    });
    return teams;
  }

  assignable.forEach((member, index) => {
    teams[index % count].memberIds.push(member.id);
  });

  return teams;
};

export const joinTeamsWithContacts = (
  teams: Array<{ _id: string; name: string; memberIds: string[] }>,
  contacts: ContactListMember[],
  options?: { hideEmpty?: boolean }
): Team[] => {
  const byId = new Map(contacts.map((contact) => [contact.id, contact]));
  const joined = teams.map((team) => ({
    id: team._id,
    name: team.name,
    members: team.memberIds.map((id) => {
      const contact = byId.get(id);
      const declined = contact ? hasDeclinedContactShare(contact) : false;
      return {
        id,
        name: contact?.name || 'Unknown member',
        company: contact?.company,
        avatarUrl: contact?.avatarUrl,
        isHidden: declined,
      };
    }),
  }));

  if (options?.hideEmpty === false) {
    return joined;
  }

  return joined.filter((team) => team.members.length > 0);
};
